import type { TFunction } from 'react-i18next';

import type { MapEditorRoom } from './mapEditorRooms';
import type {
  MapLayoutRefusal,
  MapLayoutRefusalReason,
} from './mapEditorUnreal';

// What to tell the author about a segment the authority would not place.
//
// Worded as advice, which is a correctness decision rather than a tone one. A turning corridor's
// overlap box is a single rectangle drawn around the whole L, so two corridors running parallel two
// metres apart can graze each other's boxes and the second is turned down for room the walls do not
// actually take up. Wording that said the segment "cannot be placed" was therefore telling the author
// something untrue about their layout — the segment is fine, it just needs a nudge — and left them
// with nothing to try. So each sentence names what happened and the next thing to do.
//
// The one exception is `start-not-first` off an author's own row, which really is a fault in this hub
// and says so: an author cannot act on it, and pretending otherwise sends them looking.

/**
 * The sentence for a refused segment, in the panel that is about that segment.
 *
 * @param reason - The reason the authority reported.
 * @param translate - i18next `t`, passed in so this stays a plain function.
 * @param neighbourName - What it ran into, when the report names one. Used only by `overlap`, where
 * naming the neighbour is most of the advice — "runs into Corridor" tells the author where to look.
 * @returns The sentence, already translated.
 */
export const describeRefusalAdvice = (
  reason: MapLayoutRefusalReason,
  translate: TFunction,
  neighbourName = '',
): string => {
  if (reason === 'overlap') {
    return neighbourName
      ? translate(
          'editorMode.mapEditorAdviceOverlapNamed',
          'Runs into {{neighbour}} — move it a little, or pick a shorter one, and it goes up next time.',
          { neighbour: neighbourName },
        )
      : translate(
          'editorMode.mapEditorAdviceOverlap',
          'Runs into a segment already standing — move it a little, or pick a shorter one, and it goes up next time.',
        );
  }

  if (reason === 'chain-closed') {
    return translate(
      'editorMode.mapEditorAdviceClosed',
      'Sits after the wall that closes the walk, so it was not built. Move it above the end of the chain.',
    );
  }

  if (reason === 'start-not-first') {
    return translate(
      'editorMode.mapEditorAdviceStartNotFirst',
      'This segment can only be the entrance, and the entrance comes first. Remove it — the rest of the walk was built.',
    );
  }

  return translate(
    'editorMode.mapEditorAdviceTruncated',
    'Not attempted — the walk stopped at the segment above it. Sort that one out and this goes up with it.',
  );
};

/**
 * The 1-based segment number to quote for a refusal, as the author's own list numbers it.
 *
 * A refusal's `index` is a position in the chain that was *published*, and that chain is not the
 * author's list: the lobby is prepended to it and the cap appended, so from the moment an entrance
 * is chosen every author row sits one place further along than the row label says. UE compensates
 * for exactly this when it prepends a start row itself; a chain this hub opened is the hub's to
 * account for.
 *
 * Resolved through the placement key rather than by subtracting an offset, for the same reason the
 * per-row markers are (see findRefusalFor): a key belongs to the placement, so it answers correctly
 * whatever else was added to the chain, while an offset has to be recomputed every time the shape
 * of a published chain changes. The index is the fallback for a report that carries no keys, where
 * no lobby can have been sent either — a `role: 'start'` element needs a catalog that names one.
 *
 * @param refusal - The refusal being described.
 * @param rooms - The author's draft, in the order the list renders it.
 * @returns The segment number, 1-based.
 */
export const draftRowOf = (
  refusal: MapLayoutRefusal,
  rooms: MapEditorRoom[],
): number =>
  refusal.roomKey
    ? rooms.findIndex((room) => room.key === refusal.roomKey)
    : -1;

const draftPositionOf = (
  refusal: MapLayoutRefusal,
  rooms: MapEditorRoom[],
): number => {
  const keyed = draftRowOf(refusal, rooms);

  return (keyed >= 0 ? keyed : refusal.index) + 1;
};

/**
 * The one sentence about the whole layout: which segment could not be placed, and what happened to the
 * rest of the chain because of it.
 *
 * Needed because the per-row markers alone do not read as an answer. A refused row stops the chain dead,
 * so what the author sees is that adding segments has stopped producing geometry — and a 14px icon on one
 * row of fifteen is not what tells them why. This states it where they are already looking.
 *
 * Describes the first refusal, which is always the cause: the report lists the chain in order, and
 * everything after the first entry is a consequence of it (`chain-truncated`).
 *
 * @param refusals - Refusals for the chain currently in the list, or null when none have arrived.
 * @param nameOf - Resolves an element id to its display name.
 * @param translate - i18next `t`, passed in so this stays a plain function.
 * @param rooms - The author's draft, for numbering the segment as their own list numbers it.
 * @returns The sentence, or empty when nothing was refused.
 */
export const describeRefusalSummary = (
  refusals: MapLayoutRefusal[] | null,
  nameOf: (id: string) => string,
  translate: TFunction,
  rooms: MapEditorRoom[],
): string => {
  const cause = refusals?.[0];
  if (!cause) {
    return '';
  }

  // Counted from the whole report rather than from the cause's index: an index is a position in the
  // chain that was sent, and the number worth stating is how many rows did not get built.
  const dropped = (refusals?.length ?? 0) - 1;
  const tail =
    dropped > 0
      ? ` ${translate(
          'editorMode.mapEditorRefusedTail',
          'The {{count}} segment(s) after it were not built either.',
          { count: dropped },
        )}`
      : '';

  // The runtime's own closing wall. It sits on its own row outside the author's list, so naming a
  // segment number here would point at a row they cannot see.
  if (cause.auto) {
    return `${translate(
      'editorMode.mapEditorRefusedSummaryCap',
      'The wall that closes the layout could not be placed — the chain comes back round onto itself.',
    )}${tail}`;
  }

  const position = String(draftPositionOf(cause, rooms)).padStart(2, '0');
  const name = nameOf(cause.id);

  if (cause.reason === 'overlap') {
    return `${translate(
      'editorMode.mapEditorRefusedSummaryOverlap',
      'Segment {{position}} ({{name}}) will not fit — it lands on {{blocker}} already standing.',
      {
        position,
        name,
        blocker: cause.blockedById
          ? nameOf(cause.blockedById)
          : translate('editorMode.mapEditorRefusedBlockerUnknown', 'a segment'),
      },
    )}${tail}`;
  }

  if (cause.reason === 'chain-closed') {
    return `${translate(
      'editorMode.mapEditorRefusedSummaryClosed',
      'Segment {{position}} ({{name}}) comes after the wall that closes the layout, so it was never built.',
      { position, name },
    )}${tail}`;
  }

  // The two causes described in describeRefusal, and the position is what separates them: a shell
  // sitting on a row of the author's own list has a number to quote and can be removed, while the
  // entrance this hub prepended has no row and no number, so quoting one would point at a room that
  // has nothing to do with the refusal.
  //
  // The tail is dropped on the row variant. This refusal takes its own row and nothing else — the
  // rest of the layout is standing — so "the segments after it were not built either" would
  // contradict the sentence it is appended to.
  if (cause.reason === 'start-not-first') {
    if (draftRowOf(cause, rooms) >= 0) {
      return translate(
        'editorMode.mapEditorRefusedSummaryStartNotFirstRow',
        'Segment {{position}} ({{name}}) can only be the entrance, so it was not built where it is. Remove it — the rest of the layout is standing.',
        { position, name },
      );
    }

    return `${translate(
      'editorMode.mapEditorRefusedSummaryStartNotFirst',
      'The entrance ({{name}}) was sent in the wrong place in the chain and was not built. This is a fault in the editor; reload the page and publish again.',
      { name },
    )}${tail}`;
  }

  return `${translate(
    'editorMode.mapEditorRefusedSummaryTruncated',
    'Segment {{position}} ({{name}}) was never attempted — the chain stopped above it.',
    { position, name },
  )}${tail}`;
};

/**
 * The sentence for a landing that did not happen.
 *
 * @param failure - The code UE reported, or undefined for a failure it named no reason for. A plain
 * string rather than `MapEditorKnownLandFailure`, because a code this hub has no phrase for still
 * has to arrive here and still means the landing failed.
 * @param translate - i18next `t`, passed in so this stays a plain function.
 * @returns The sentence, already translated.
 */
export const describeLandFailure = (
  failure: string | undefined,
  translate: TFunction,
): string => {
  // Whose fault it is decides what the author is told to do, which is why these are four messages
  // rather than one with a nuance: a stale tree is fixed by publishing, a crowded segment by trying
  // again, and geometry that has not stood is nobody's to fix by retrying at all. Telling an author
  // to retry something that cannot succeed is the failure mode this vocabulary exists to avoid.
  if (failure === 'no-such-segment') {
    return translate(
      'editorMode.mapEditorLandNoSegment',
      'That segment is not standing in the world, so there was nowhere to go. Publish the walk and try again.',
    );
  }

  if (failure === 'no-floor') {
    return translate(
      'editorMode.mapEditorLandNoFloor',
      'That segment has no floor to stand on yet — its geometry has not finished building. You are still where you were.',
    );
  }

  if (failure === 'blocked') {
    return translate(
      'editorMode.mapEditorLandBlocked',
      'There was nowhere free to put you inside that segment. It may just be crowded — try again.',
    );
  }

  if (failure === 'not-arrived') {
    return translate(
      'editorMode.mapEditorLandNotArrived',
      'You were moved, but not into that segment, and the world did not say why.',
    );
  }

  // A code this hub does not know, or none at all. Says the one thing that is certainly true rather
  // than guessing which of the four it was — see MapEditorKnownLandFailure for why a new code must
  // land here rather than break the read.
  return translate(
    'editorMode.mapEditorLandFailed',
    'Could not put you in that segment. You are still where you were.',
  );
};
